import { computed, nextTick, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import type { NavigationItem } from '@/shared/lib';
import {
  isCustomFolderCode,
  isModuleCode,
  toModuleFolderNavigationItemId,
  toModuleNavigationItemId,
} from './mapVisibleTreeToNavigation';
import { MODULE_MATRIX_REVISION_ROUTE_NAME } from './useModuleMatrixRevisionScope';
import {
  findVisibleFolderByCode,
  findVisibleModuleByCode,
  loadVisibleDirectoryTree,
} from './useVisibleDirectoryTree';

export const MODULE_CATEGORY_ID = 'module';

const FILTERED_LIST_ROUTE_NAME = 'FilteredList';

interface ModuleRouteNavigationTarget {
  selectedCategory: Ref<string>;
  selectedModules: Ref<NavigationItem[]>;
  setSelectedItem: (itemId: string) => void;
  addExpandedItem: (itemId: string) => void;
}

interface ModuleTreeLocation {
  moduleItemId: string;
  expandedItemIds: string[];
  selectedItemId: string;
}

function resolveModuleTreeLocation(code: string): ModuleTreeLocation | null {
  if (isModuleCode(code)) {
    const module = findVisibleModuleByCode(code);
    if (!module) return null;
    const moduleItemId = toModuleNavigationItemId(module.moduleId);
    // The module itself is not expanded: the click that opened this route may have just collapsed it.
    return { moduleItemId, expandedItemIds: [], selectedItemId: moduleItemId };
  }

  const entry = findVisibleFolderByCode(code);
  // A fixed prefix folder also answers to an F code, but it lives in the Tipo de Documento tree.
  if (!entry || entry.moduleId === null) return null;
  const moduleItemId = toModuleNavigationItemId(entry.moduleId);
  return {
    moduleItemId,
    expandedItemIds: [
      moduleItemId,
      ...entry.path.slice(0, -1).map((segment) => toModuleFolderNavigationItemId(segment.id)),
    ],
    selectedItemId: toModuleFolderNavigationItemId(entry.folder.id),
  };
}

function readFilteredListCode(itemName: unknown): string | null {
  const code = Array.isArray(itemName) ? itemName.join('/') : itemName;
  return typeof code === 'string' && (isModuleCode(code) || isCustomFolderCode(code))
    ? code
    : null;
}

/**
 * Mirrors a module or module folder route (its listing or its matrix revision page) in the Módulo
 * tree, so a deep link or a breadcrumb click lands with the item highlighted.
 */
export function useModuleRouteNavigation(target: ModuleRouteNavigationTarget) {
  const route = useRoute();

  const routeCode = computed(() => {
    if (route.name === MODULE_MATRIX_REVISION_ROUTE_NAME) {
      const { folderCode } = route.params;
      return typeof folderCode === 'string' && isCustomFolderCode(folderCode) ? folderCode : null;
    }
    if (route.name === FILTERED_LIST_ROUTE_NAME) {
      return readFilteredListCode(route.params.itemName);
    }
    return null;
  });

  async function syncFromRoute() {
    const code = routeCode.value;
    if (!code) return;

    try {
      await loadVisibleDirectoryTree();
    } catch {
      return;
    }
    if (routeCode.value !== code) return;

    const location = resolveModuleTreeLocation(code);
    if (!location) return;

    if (target.selectedCategory.value !== MODULE_CATEGORY_ID) {
      target.selectedCategory.value = MODULE_CATEGORY_ID;
      // Switching away from another category makes NavigationContent reset the shared tree in a
      // pre-flush watcher, which would erase a selection written in the same tick.
      await nextTick();
    }

    const selectedModuleId = target.selectedModules.value[0]?.id;
    if (selectedModuleId !== undefined && String(selectedModuleId) !== location.moduleItemId) {
      target.selectedModules.value = [];
    }

    location.expandedItemIds.forEach((itemId) => target.addExpandedItem(itemId));
    target.setSelectedItem(location.selectedItemId);
  }

  watch(routeCode, () => {
    void syncFromRoute();
  });

  return { syncFromRoute };
}
